import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { CrossDecoration, ChristianIcon, ScriptureQuote } from './ChristianElements';

const HeroSection: React.FC = () => {
  const { t } = useTranslation();

  const scrollToNext = () => {
    const nextSection = document.getElementById('intro-section');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background with Christian elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-900/80 to-primary-700/80 holy-land-bg">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: `url('/images/woodhall.jpg')`
          }}
        />
        
        {/* Floating Christian symbols */}
        <div className="absolute top-20 left-4 md:left-20 text-gold-500/20 text-4xl md:text-6xl animate-pulse">
          ✞
        </div>
        <div className="absolute top-40 right-4 md:right-32 text-gold-500/20 text-3xl md:text-4xl animate-pulse delay-1000">
          🕊
        </div>
        <div className="absolute bottom-40 left-4 md:left-32 text-gold-500/20 text-4xl md:text-5xl animate-pulse delay-2000">
          🫒
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container-max text-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Christian cross decoration */}
          <div className="flex justify-center mb-4 md:mb-6">
            <ChristianIcon icon="cross" size="lg" className="text-gold-400" />
          </div>
          
          <CrossDecoration size="lg" className="mb-4 md:mb-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl lg:text-7xl font-serif font-bold text-brown leading-tight">
              {t('hero.title')}
            </h1>
          </CrossDecoration>
          
          <p className="text-lg md:text-xl lg:text-3xl text-brown mb-6 md:mb-8 max-w-3xl mx-auto font-serif leading-relaxed px-2">
            {t('hero.subtitle')}
          </p>

          {/* Scripture quote */}
          <div className="mb-6 md:mb-8 max-w-2xl mx-auto px-2">
            <ScriptureQuote 
              verse={t('scriptures.comfort.verse')}
              reference={t('scriptures.comfort.reference')}
              className="bg-white/10 backdrop-blur-sm border-gold-400/50 text-white"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center px-4">
            <Link
              to="/contact"
              className="btn-primary text-base md:text-lg px-6 md:px-8 py-3 md:py-4 inline-flex items-center shadow-lg hover:shadow-gold-500/25 transition-all duration-300 w-full sm:w-auto justify-center min-h-[48px] touch-manipulation"
            >
              <ChristianIcon icon="dove" size="sm" className="mr-2" />
              {t('hero.cta')}
            </Link>
            
            <Link
              to="/story"
              className="btn-secondary text-base md:text-lg px-6 md:px-8 py-3 md:py-4 inline-flex items-center border-gold-400 text-gold-400 hover:bg-gold-400 hover:text-white transition-all duration-300 w-full sm:w-auto justify-center min-h-[48px] touch-manipulation"
            >
              {t('hero.learnMore')}
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollToNext}
        className="absolute bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 text-white hover:text-primary-200 transition-colors duration-300 animate-bounce"
        aria-label="Scroll to next section"
      >
        <ChevronDown className="w-6 h-6 md:w-8 md:h-8" />
      </button>
    </section>
  );
};

export default HeroSection;
